import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { getSelectedClientId } from '../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../data/store'
import { useConversationsData } from '../data/hooks/useConversationsData'
import { getSelectedConversationId } from '../data/messagesSelectors'
import { clearSelectedConversation, setSelectedConversation } from '../data/messagesSlice'

// Client-page messengers share one Redux conversation selection, which the chart also uses for its
// new-message alerts. Give the chart back its previous selection, or its patient conversation if
// that conversation only becomes available after this messenger mounts.
// Restores on unmount; call restorePreviousConversation to restore earlier (e.g. before navigating away).
// Mount the messenger only once isReady: its own mount effects run first and would change the selection
// before it is recorded.
export const useRestorePreviousConversation = () => {
  const dispatch = useAppDispatch()
  const clientId = useSelector(getSelectedClientId)
  const selectedConversationId = useSelector(getSelectedConversationId)
  const { data: conversations } = useConversationsData()
  const previousConversationId = useRef<string | null>(null)
  const ownSelection = useRef<string | null>(null)
  const ownsSelection = useRef(false)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    ownSelection.current = selectedConversationId
  }, [selectedConversationId])

  const restorePreviousConversation = useCallback(() => {
    if (!ownsSelection.current) return
    ownsSelection.current = false
    dispatch((dispatch, getState) => {
      const state = getState()
      if (getSelectedClientId(state) !== clientId) return
      const currentId = getSelectedConversationId(state)
      // Do not overwrite a selection made outside this messenger. A null selection is also
      // ours while the messenger shows a draft or handles a rejected conversation.
      if (currentId !== null && currentId !== ownSelection.current) return
      dispatch(
        previousConversationId.current
          ? setSelectedConversation(previousConversationId.current)
          : clearSelectedConversation(),
      )
    })
  }, [clientId, dispatch])

  useEffect(() => {
    // Read after an outgoing messenger's cleanup restores the surrounding selection.
    previousConversationId.current = dispatch((_, getState) => getSelectedConversationId(getState()))
    ownsSelection.current = true
    setIsReady(true)
    return restorePreviousConversation
  }, [dispatch, restorePreviousConversation])

  useEffect(() => {
    // The chart defaults its patient conversation only once per client. If it loads or is created
    // while the popover is open, restore that default instead of the null captured at opening.
    if (previousConversationId.current === null) {
      previousConversationId.current =
        conversations?.find((conversation) => conversation.patient_lyra_id === clientId)?.conversation_id ?? null
    }
  }, [clientId, conversations])

  return { isReady, restorePreviousConversation }
}
